import type { ReactNode } from "react";

import "./emptyState.css";

/**
 * "There is nothing here yet, and here is why."
 *
 * Thirteen screens rendered the same two paragraphs in a box — a lead that
 * names the absence and a detail that says what would fill it — each with its
 * own copy of the markup. The markup is now one component; the look is mostly
 * still the screen's, because the copies had not agreed on one. See the header
 * of emptyState.css for what is shared and what each screen keeps.
 *
 * `className` is the screen's own block class (`estate__empty`,
 * `gallery__empty`, …). It carries the box — padding, border, width — and any
 * override of the lead or detail, scoped as `.gallery__empty .empty-state__lead`.
 * `children` is for the one screen that puts a control under the copy: an
 * empty contact list is precisely who needs the add button.
 */
export function EmptyState({
  lead,
  detail,
  className = "",
  role,
  children,
}: {
  lead: ReactNode;
  detail?: ReactNode;
  className?: string;
  role?: string;
  children?: ReactNode;
}) {
  return (
    <section className={`empty-state${className ? ` ${className}` : ""}`} role={role}>
      <p className="empty-state__lead">{lead}</p>
      {detail !== undefined && <p className="empty-state__detail">{detail}</p>}
      {children}
    </section>
  );
}
